/**
 * Prior-jobs editor for a walker or a trainer.
 *
 * `years_of_experience` next door is one number and answers "how long". This answers "doing
 * what, for whom" — the part a KYC reviewer and a worried pet owner actually care about for
 * somebody who will be alone with their dog.
 *
 * Two things the shape forces on this component:
 *
 *  - The API REPLACES the stored list with whatever is sent, so the form always holds every
 *    row and submits all of them. There is no per-row save, and a row removed here is a row
 *    deleted on save.
 *  - Dates are month precision (`YYYY-MM-01`), because that is all anyone remembers
 *    accurately. The inputs are `type="month"` and the `-01` is appended on the way out.
 */
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TextField } from '@/components/text-field';

/** One row as the form holds it — all strings, because that is what inputs give back. */
export type ExperienceRow = {
  role: string;
  organisation: string;
  /** `YYYY-MM`, from a month input. */
  from_month: string;
  /** `YYYY-MM`, or empty while still in the role. */
  to_month: string;
  description: string;
};

export const EMPTY_EXPERIENCE_ROW: ExperienceRow = {
  role: '',
  organisation: '',
  from_month: '',
  to_month: '',
  description: '',
};

/** How many rows the API will accept. Mirrors MAX_EXPERIENCE_ENTRIES on the backend. */
export const MAX_EXPERIENCE_ROWS = 15;

/** A stored entry as the details endpoint returns it. */
export type SavedExperience = {
  id: string;
  role: string;
  organisation: string | null;
  /** `YYYY-MM-DD`. */
  from_month: string;
  to_month: string | null;
  is_current: boolean;
  description: string | null;
  months: number;
};

/** Turn saved entries back into editable rows, trimming the day off each date. */
export function rows_from_saved(saved: SavedExperience[]): ExperienceRow[] {
  return saved.map((e) => ({
    role: e.role,
    organisation: e.organisation ?? '',
    from_month: e.from_month.slice(0, 7),
    to_month: e.to_month ? e.to_month.slice(0, 7) : '',
    description: e.description ?? '',
  }));
}

/** A row the vendor started and then left entirely blank is not an entry — it is a mis-click. */
export function is_blank_row(r: ExperienceRow): boolean {
  return (
    !r.role.trim() && !r.organisation.trim() && !r.from_month && !r.to_month && !r.description.trim()
  );
}

export type ExperiencePayloadEntry = {
  role: string;
  organisation?: string | null;
  from_month: string;
  to_month?: string | null;
  description?: string | null;
};

/**
 * Rows → request payload. Blank rows are dropped, and the month inputs are widened to the
 * first of the month, which is the precision the column stores.
 */
export function experience_payload(rows: ExperienceRow[]): ExperiencePayloadEntry[] {
  return rows.filter((r) => !is_blank_row(r)).map((r) => ({
    role: r.role.trim(),
    organisation: r.organisation.trim() ? r.organisation.trim() : null,
    from_month: `${r.from_month}-01`,
    to_month: r.to_month ? `${r.to_month}-01` : null,
    description: r.description.trim() ? r.description.trim() : null,
  }));
}

/**
 * Validate client-side, mirroring the server's rules so the vendor is told before a round-trip.
 * Returns a message for the first offending row, or null when every row is fine.
 */
export function experience_error(rows: ExperienceRow[]): string | null {
  const filled = rows.filter((r) => !is_blank_row(r));
  if (filled.length > MAX_EXPERIENCE_ROWS) {
    return `At most ${MAX_EXPERIENCE_ROWS} experience entries.`;
  }
  const this_month = new Date().toISOString().slice(0, 7);
  for (const [i, r] of filled.entries()) {
    const where = `Experience #${i + 1}`;
    if (r.role.trim().length < 2) return `${where}: role must be at least 2 characters.`;
    if (!r.from_month) return `${where}: a start month is required.`;
    if (r.from_month > this_month) return `${where}: the start month cannot be in the future.`;
    if (r.to_month && r.to_month < r.from_month) {
      return `${where}: the end month cannot be before the start month.`;
    }
  }
  return null;
}

type Props = {
  /** Word the section for the module — "walking" reads better than "working" for a walker. */
  noun: string;
  rows: ExperienceRow[];
  on_change: (rows: ExperienceRow[]) => void;
  /** Prefix for data-testid hooks, e.g. `walker`. */
  testid_prefix: string;
  disabled?: boolean;
};

export function ExperienceRepeater({ noun, rows, on_change, testid_prefix, disabled }: Props) {
  const at_max = rows.length >= MAX_EXPERIENCE_ROWS;

  function update_row(index: number, patch: Partial<ExperienceRow>) {
    on_change(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function remove_row(index: number) {
    on_change(rows.filter((_, i) => i !== index));
  }

  return (
    <div className="flex flex-col gap-3" data-testid={`${testid_prefix}-experience`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <Label>Work experience</Label>
          <p className="mt-1 text-sm text-muted-foreground">
            Optional, but it is what a pet owner reads before trusting you with {noun}. Leave the
            end month empty for a job you are still doing.
          </p>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground" data-testid={`${testid_prefix}-experience-count`}>
          {rows.length}/{MAX_EXPERIENCE_ROWS}
        </span>
      </div>

      {rows.length === 0 && (
        <p className="rounded-lg border border-dashed border-input px-3 py-4 text-sm text-muted-foreground">
          No experience added yet.
        </p>
      )}

      {rows.map((row, i) => (
        <div
          key={i}
          className="flex flex-col gap-4 rounded-lg border border-input bg-card p-4"
          data-testid={`${testid_prefix}-experience-row-${i}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Experience #{i + 1}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => remove_row(i)}
              data-testid={`${testid_prefix}-experience-remove-${i}`}
              aria-label={`Remove experience ${i + 1}`}
            >
              <Trash2 className="h-4 w-4" />
              Remove
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField
              label="Role"
              name={`experience_role_${i}`}
              data-testid={`${testid_prefix}-experience-role-${i}`}
              value={row.role}
              onChange={(e) => update_row(i, { role: e.target.value })}
              maxLength={120}
              placeholder="Dog walker"
            />
            <TextField
              label="Organisation (optional)"
              name={`experience_org_${i}`}
              data-testid={`${testid_prefix}-experience-org-${i}`}
              value={row.organisation}
              onChange={(e) => update_row(i, { organisation: e.target.value })}
              maxLength={160}
              placeholder="Happy Paws Bengaluru"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor={`experience_from_${i}`}>From</Label>
              <input
                id={`experience_from_${i}`}
                type="month"
                data-testid={`${testid_prefix}-experience-from-${i}`}
                value={row.from_month}
                max={new Date().toISOString().slice(0, 7)}
                onChange={(e) => update_row(i, { from_month: e.target.value })}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor={`experience_to_${i}`}>To (leave empty if current)</Label>
              <input
                id={`experience_to_${i}`}
                type="month"
                data-testid={`${testid_prefix}-experience-to-${i}`}
                value={row.to_month}
                min={row.from_month || undefined}
                onChange={(e) => update_row(i, { to_month: e.target.value })}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          <TextField
            label="What did you do? (optional)"
            name={`experience_desc_${i}`}
            data-testid={`${testid_prefix}-experience-desc-${i}`}
            value={row.description}
            onChange={(e) => update_row(i, { description: e.target.value })}
            maxLength={1000}
            placeholder="Solo and group walks for 20+ regular clients, mostly large breeds."
          />
        </div>
      ))}

      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || at_max}
          onClick={() => on_change([...rows, { ...EMPTY_EXPERIENCE_ROW }])}
          data-testid={`${testid_prefix}-experience-add`}
        >
          <Plus className="h-4 w-4" />
          Add experience
        </Button>
        {at_max && (
          <p className="mt-2 text-xs text-muted-foreground">
            That is the maximum of {MAX_EXPERIENCE_ROWS} entries.
          </p>
        )}
      </div>
    </div>
  );
}
